import React, { useState, useEffect } from 'react';
import { X, Check, CreditCard, Infinity, Clock, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import { useGetPackagesQuery, usePurchaseCreditsMutation } from '../../store/services/api';
import { toast } from 'react-toastify';
import type { Package } from '../../types/billing';

interface BuyMoreCreditsProps {
  isOpen: boolean;
  onClose: () => void;
}

// API yanıtındaki açıklamayı özellik listesine dönüştüren yardımcı fonksiyon
const parseFeatures = (description: string): string[] => {
  if (!description) return [];
  return description.split(', ').filter(Boolean);
};

const BuyMoreCredits = ({ isOpen, onClose }: BuyMoreCreditsProps) => {
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  
  // RTK Query hooks
  const { data: packages = [], isLoading } = useGetPackagesQuery(undefined, {
    skip: !isOpen // Modal açık değilse sorgu yapma
  });
  
  const [purchaseCredits, { isLoading: isPurchasing }] = usePurchaseCreditsMutation();

  // Add ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    
    // Prevent body scrolling when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handlePurchase = async () => {
    if (!selectedPackage) return;
    
    try {
      const result = await purchaseCredits({
        packageId: selectedPackage,
        customerEmail: localStorage.getItem('user_email') || undefined
      }).unwrap();
      
      window.location.href = result.url;
    } catch (error) {
      toast.error('Failed to process your purchase. Please try again later.');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-[2px]"
      style={{ isolation: 'isolate' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        className="bg-white rounded-lg w-full max-w-xl shadow-lg relative" 
        onClick={(e) => e.stopPropagation()}
        style={{ transform: 'translateZ(0)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-medium mb-0.5">Buy Image Credits</h2>
            <p className="text-xs text-gray-500">Choose a package that suits your needs</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Package Options */}
        <div className="p-4 flex flex-col gap-3">
          {isLoading ? (
            <div className="py-10 flex flex-col items-center justify-center text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin mb-2" />
              <p className="text-sm">Loading packages...</p>
            </div>
          ) : packages.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p>No packages available. Please try again later.</p>
            </div>
          ) : (
            packages.map((pkg) => (
              <div
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg.id)}
                className={`p-3 rounded-md border cursor-pointer transition-all relative ${
                  selectedPackage === pkg.id
                    ? 'border-[#84cc16] bg-[#84cc16]/5'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                {pkg.isPopular && (
                  <div className="absolute -top-2 -right-2 bg-[#FF4444] text-white text-[10px] px-2 py-0.5 rounded-full">
                    POPULAR
                  </div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-sm font-medium">
                      {pkg.name}
                    </h3>
                  </div>
                  <div className="text-base font-medium">${pkg.price.toFixed(2)}</div>
                </div>
                <ul className="flex flex-col gap-1">
                  {parseFeatures(pkg.description).map((feature, index) => (
                    <li key={index} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <Check className="w-3.5 h-3.5 text-[#84cc16]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <CreditCard className="w-3.5 h-3.5" />
              Secure payment via Stripe
            </div>
            <Button
              onClick={handlePurchase}
              disabled={!selectedPackage || isPurchasing || isLoading}
              className="h-8 text-xs px-4"
            >
              {isPurchasing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  Processing...
                </>
              ) : (
                'Purchase Credits'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyMoreCredits;